'use client'

import type { Locale } from '@/i18n/config'
import { BACKEND_API_URL, OPENAI_DOMAIN_KEY } from '@/lib/constant'
import { User } from '@/lib/types'
import { ChatKit, ChatKitOptions, useChatKit } from '@openai/chatkit-react'
import { useLocale } from 'next-intl'
import React, { useEffect } from 'react'
import { TChatKitConstants } from '../_lib/types'
import { mapLocaleToChatkitLocale } from '../_lib/utils'
import {
  ChatKitInitializationError,
  ChatKitLoadingState,
  ChatKitScriptError,
} from './chatkit-error-states'
import { useChatkitScriptContext } from '@/providers/chatkit-script-provider'
import { SpacingVertical } from '@/components/ui/spacing'

export type ChatKitWorkflowKey =
  | 'ebook'
  | 'blog'
  | 'market_research'
  | 'design'
  | 'social_media'
  | 'business_naming'
  | 'business_plan'

interface IChatKitPageWrapperProps {
  /**
   * Current user object
   */
  user: User
  /**
   * User's API key for ChatKit authentication
   */
  userApiKey: string
  /**
   * Workflow key to identify the specific ChatKit workflow
   */
  workflowKey: ChatKitWorkflowKey
  /**
   * Localized ChatKit constants (greeting, prompts, placeholder)
   */
  constants: TChatKitConstants
  /**
   * Translation function for UI text
   */
  t: (key: string) => string
  /**
   * Icon component to display in header
   */
  icon: React.ComponentType<{ className?: string }>
  /**
   * Optional project ID to attach project context to ChatKit requests
   */
  projectId?: string
  /**
   * Whether to hide the header (title and description)
   */
  hideHeader?: boolean
}

/**
 * Shared ChatKit page wrapper component
 * Handles script loading, ChatKit initialization, and common UI structure
 */
export function ChatKitPageWrapper({
  user,
  userApiKey,
  workflowKey,
  constants,
  t,
  icon: Icon,
  projectId,
  hideHeader = false,
}: IChatKitPageWrapperProps) {
  const locale = useLocale() as Locale
  const chatkitLocale = mapLocaleToChatkitLocale(locale)

  // Get ChatKit script loading state from context
  const { isChatkitLoaded, scriptError, isLoadingScript } =
    useChatkitScriptContext()

  const { greeting, starterPrompts, placeholder } = constants

  console.log(`🟢 ChatKit ${workflowKey} initialized:`, {
    userId: user._id.toString(),
    backendUrl: BACKEND_API_URL,
    apiKeyLength: userApiKey?.length || 0,
    locale: chatkitLocale,
    workflowKey,
  })

  const options: Partial<ChatKitOptions> = {
    locale: chatkitLocale,
    theme: {
      colorScheme: 'dark',
      color: {
        accent: {
          primary: '#7A53F4',
          level: 2,
        },
      },
      radius: 'round',
      density: 'compact',
      typography: {
        baseSize: 14,
        fontFamily: 'Poppins, "Poppins Fallback", sans-serif',
      },
    },
    composer: {
      attachments: {
        enabled: true,
        maxSize: 20 * 1024 * 1024, // 20MB per file
        maxCount: 3,
        accept: { 'application/pdf': ['.pdf'], 'image/*': ['.png', '.jpg'] },
      },
    },
  }

  // Initialize ChatKit with custom endpoint and dark theme
  const chatKit = useChatKit({
    api: {
      url: `${BACKEND_API_URL}/v1/chatkit`,
      domainKey: OPENAI_DOMAIN_KEY,
      fetch: async (url: RequestInfo | URL, options: RequestInit = {}) => {
        const urlString = typeof url === 'string' ? url : url.toString()
        console.log('🔵 ChatKit fetch called:', {
          url: urlString,
          method: options.method || 'GET',
          headers: {
            ...options.headers,
            authorization: userApiKey ? 'SET' : 'NOT_SET',
            'x-workflow-key': workflowKey,
            'x-user-id': user._id.toString(),
            ...(projectId && { 'x-project-id': projectId }),
          },
          bodyPreview:
            options.body instanceof FormData
              ? 'FormData'
              : options.body
                ? JSON.stringify(options.body).substring(0, 100)
                : 'none',
        })
        try {
          const response = await fetch(url, {
            ...options,
            headers: {
              ...(options as RequestInit).headers,
              authorization: `${userApiKey}${projectId ? `***${projectId}` : ''}`,
              'x-workflow-key': workflowKey,
              'x-user-id': user._id.toString(),
              'x-user-email': user.email || '',
              ...(projectId && { 'x-project-id': projectId }),
            },
          })

          console.log('🟢 ChatKit fetch response:', {
            url: urlString,
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            headers: Object.fromEntries(response.headers.entries()),
          })

          return response
        } catch (err) {
          console.error('🔴 ChatKit fetch error:', {
            url: urlString,
            error: err,
            errorMessage: err instanceof Error ? err.message : String(err),
            errorStack: err instanceof Error ? err.stack : undefined,
          })
          throw err
        }
      },
      uploadStrategy: { type: 'two_phase' },
    },
    startScreen: {
      greeting,
      prompts: starterPrompts,
    },
    composer: {
      placeholder,
    },
    onError: ({ error }: { error: Error }) => {
      console.error('🔴 ChatKit onError callback:', {
        error,
        message: error.message,
        stack: error.stack,
      })
    },
    ...options,
  })

  console.log('🟣 ChatKit hook result:', {
    hasControl: !!chatKit.control,
    controlKeys: chatKit.control ? Object.keys(chatKit.control) : [],
  })

  /**
   * Hide the setup prompt element inside the ChatKit iframe
   * Uses attribute selectors since the class names are dynamic
   */
  useEffect(() => {
    if (!chatKit.control) {
      return
    }

    const hideSetupPrompt = () => {
      // Find the ChatKit iframe
      const iframe = document.querySelector('.ck-iframe') as HTMLIFrameElement
      if (!iframe) {
        return
      }

      try {
        const iframeDocument =
          iframe.contentDocument || iframe.contentWindow?.document
        if (!iframeDocument) {
          return
        }

        // Select element using data attributes (class-independent, excluding buttons)
        const elementToHide = iframeDocument.querySelector(
          'header [data-exit-animation="slide-full"][data-has-arrow]:not(button)',
        ) as HTMLElement

        if (elementToHide) {
          elementToHide.style.display = 'none'
          console.log('🟢 Hidden setup prompt element')
        }
      } catch (error) {
        console.error('🔴 Error accessing iframe content:', error)
      }
    }

    // Wait for iframe to load, then hide the element
    const iframe = document.querySelector('.ck-iframe') as HTMLIFrameElement
    if (iframe) {
      if (iframe.contentDocument?.readyState === 'complete') {
        // Iframe already loaded, hide immediately
        hideSetupPrompt()
      } else {
        // Wait for iframe to load
        iframe.addEventListener('load', hideSetupPrompt)
        return () => {
          iframe.removeEventListener('load', hideSetupPrompt)
        }
      }
    }

    // Also try after a short delay in case the element is added dynamically
    const timeoutId = setTimeout(hideSetupPrompt, 1000)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [chatKit.control])

  return (
    <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 bg-transparent rounded-lg">
      {/* Header */}
      {!hideHeader && (
        <div className="mb-3 sm:mb-4 lg:mb-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-start sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Icon className="size-5 sm:size-6 text-white flex-shrink-0" />
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white truncate">
                  {t('title')}
                </h1>
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm md:text-base leading-relaxed">
              {t('description')}
            </p>
          </div>
        </div>
      )}

      {/* ChatKit Interface */}
      {scriptError ? (
        <ChatKitScriptError t={t} errorMessage={scriptError} />
      ) : !isChatkitLoaded || isLoadingScript ? (
        <ChatKitLoadingState t={t} />
      ) : chatKit.control ? (
        <ChatKit
          control={chatKit.control}
          className="block h-[70vh] w-full bg-background rounded-lg overflow-hidden"
        />
      ) : (
        <ChatKitInitializationError t={t} />
      )}
    </div>
  )
}
